﻿'use client';

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ArticleDisplay from '@/components/ArticleDisplay';
import PageFrame from '@/components/PageFrame';
import type { Article } from '@/components/ArticleCard';
import { sendGAEvent } from '@next/third-parties/google';
import { buildIssueWarmQueue, shouldWarmManyIssues } from '@/lib/issuePrefetchPlan';
import type { NetworkInformationLike } from '@/lib/issuePrefetchPlan';

interface Issue {
    id: string;
    edition_code: string;
    edition_name: string;
    article_count: number;
    published_at: string;
    released_at?: string;
    status: 'preview' | 'released';
    date: string;
}

interface HomePageClientProps {
    issues?: Issue[];
    initialArticles?: HomeArticle[];
}

interface FetchIssueOptions {
    showLoading?: boolean;
}

type HomeArticle = Partial<Article> & {
    article_id?: string;
    zeroEchoScore?: number;
    impactScore?: number;
};

// Award calculation for a list of articles
function calculateAwards(articles: HomeArticle[]): HomeArticle[] {
    const processed = articles.map(a => ({ ...a, awards: [] as string[] }));

    // Combined Score sorting (Today's Headline)
    const byCombo = [...processed].sort((a, b) => {
        const zeA = a.zero_echo_score ?? a.zeroEchoScore ?? 10;
        const zeB = b.zero_echo_score ?? b.zeroEchoScore ?? 10;
        const isA = a.impact_score ?? a.impactScore ?? 0;
        const isB = b.impact_score ?? b.impactScore ?? 0;
        return ((10 - zeB) + isB) - ((10 - zeA) + isA);
    });

    // ZS sorting (Zero Echo Award - lower is better)
    const byZS = [...processed].sort((a, b) => {
        const zeA = a.zero_echo_score ?? a.zeroEchoScore ?? 10;
        const zeB = b.zero_echo_score ?? b.zeroEchoScore ?? 10;
        const isA = a.impact_score ?? a.impactScore ?? 0;
        const isB = b.impact_score ?? b.impactScore ?? 0;
        const zsDiff = zeA - zeB;
        if (Math.abs(zsDiff) < 0.01) return isB - isA;
        return zsDiff;
    });

    // Impact Score sorting (Hot Topic)
    const byIS = [...processed].sort((a, b) => {
        const isA = a.impact_score ?? a.impactScore ?? 0;
        const isB = b.impact_score ?? b.impactScore ?? 0;
        return isB - isA;
    });

    if (byCombo.length > 0) byCombo[0].awards.push("Today's Headline");
    if (byZS.length > 0 && !byZS[0].awards.includes("Zero Echo Award")) byZS[0].awards.push("Zero Echo Award");
    if (byIS.length > 0 && !byIS[0].awards.includes("Hot Topic")) byIS[0].awards.push("Hot Topic");

    // Sort by awards first, then combined score
    return processed.sort((a, b) => {
        const aAwards = a.awards?.length ?? 0;
        const bAwards = b.awards?.length ?? 0;
        if (bAwards !== aAwards) return bAwards - aAwards;
        const zeA = a.zero_echo_score ?? a.zeroEchoScore ?? 10;
        const zeB = b.zero_echo_score ?? b.zeroEchoScore ?? 10;
        const isA = a.impact_score ?? a.impactScore ?? 0;
        const isB = b.impact_score ?? b.impactScore ?? 0;
        return ((10 - zeB) + isB) - ((10 - zeA) + isA);
    });
}

function scheduleIssueWarm(callback: () => void): () => void {
    if (typeof window === 'undefined') return () => {};

    if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(callback, { timeout: 1500 });
        return () => window.cancelIdleCallback(id);
    }

    const id = globalThis.setTimeout(callback, 800);
    return () => globalThis.clearTimeout(id);
}

function wait(ms: number): Promise<void> {
    return new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

export default function HomePageClient({ issues = [], initialArticles }: HomePageClientProps) {
    // Sort issues by edition_code descending
    const sortedIssues = useMemo(() =>
        [...issues].sort((a, b) => b.edition_code.localeCompare(a.edition_code)),
        [issues]
    );
    const sortedIssueIds = useMemo(() => sortedIssues.map(i => i.id), [sortedIssues]);
    const issueMap = useMemo(() => {
        const map: Record<string, Issue> = {};
        sortedIssues.forEach(i => { map[i.id] = i; });
        return map;
    }, [sortedIssues]);

    // Client-side article cache (per issue), pre-populated with SSR latest issue
    const [articleCache, setArticleCache] = useState<Record<string, HomeArticle[]>>(() => {
        if (initialArticles && initialArticles.length > 0 && sortedIssueIds[0]) {
            return { [sortedIssueIds[0]]: calculateAwards(initialArticles) };
        }
        return {};
    });
    const articleCacheRef = useRef<Record<string, HomeArticle[]>>(articleCache);
    const inFlightFetchesRef = useRef<Record<string, Promise<void>>>({});
    const initialViewRecordedRef = useRef(false);
    const [loadingIssueId, setLoadingIssueId] = useState<string | null>(null);

    // Current selected issue index
    const [currentIssueIndex, setCurrentIssueIndex] = useState(0);

    const currentIssueId = sortedIssueIds.length > 0 ? sortedIssueIds[currentIssueIndex] : null;
    const currentIssue = currentIssueId ? issueMap[currentIssueId] : null;
    const currentArticles = currentIssueId ? (articleCache[currentIssueId] || []) : [];

    // Previous/Next issue
    const prevIssueId = currentIssueIndex < sortedIssueIds.length - 1 ? sortedIssueIds[currentIssueIndex + 1] : null;
    const nextIssueId = currentIssueIndex > 0 ? sortedIssueIds[currentIssueIndex - 1] : null;
    const prevIssue = prevIssueId ? issueMap[prevIssueId] : null;
    const nextIssue = nextIssueId ? issueMap[nextIssueId] : null;
    const isCurrentIssueLoading = currentIssueId !== null && loadingIssueId === currentIssueId;

    useEffect(() => {
        articleCacheRef.current = articleCache;
    }, [articleCache]);

    // Fetch articles for a specific issue (with client cache)
    const fetchArticlesForIssue = useCallback(async (issueId: string, options: FetchIssueOptions = {}) => {
        if (articleCacheRef.current[issueId]?.length) return;

        const existingRequest = inFlightFetchesRef.current[issueId];
        if (existingRequest) {
            if (options.showLoading) setLoadingIssueId(issueId);
            try {
                await existingRequest;
            } catch (err) {
                console.error(`Failed to fetch articles for ${issueId}:`, err);
            } finally {
                if (options.showLoading) {
                    setLoadingIssueId(current => current === issueId ? null : current);
                }
            }
            return;
        }

        if (options.showLoading) setLoadingIssueId(issueId);

        const request = (async () => {
            const res = await fetch(`/api/publications/view?publish_id=${encodeURIComponent(issueId)}`);
            if (!res.ok) {
                throw new Error(`Failed to fetch ${issueId}: ${res.status}`);
            }

            const data = await res.json() as { success?: boolean; articles?: HomeArticle[] };
            if (data.success && Array.isArray(data.articles)) {
                const withAwards = calculateAwards(data.articles);
                setArticleCache(prev => {
                    if (prev[issueId]?.length) return prev;

                    const next = { ...prev, [issueId]: withAwards };
                    articleCacheRef.current = next;
                    return next;
                });
            }
        })();

        inFlightFetchesRef.current[issueId] = request;
        try {
            await request;
        } catch (err) {
            console.error(`Failed to fetch articles for ${issueId}:`, err);
        } finally {
            delete inFlightFetchesRef.current[issueId];
            if (options.showLoading) {
                setLoadingIssueId(current => current === issueId ? null : current);
            }
        }
    }, []);

    const prefetchIssue = useCallback((issueId: string | null | undefined) => {
        if (!issueId) return;
        void fetchArticlesForIssue(issueId, { showLoading: false });
    }, [fetchArticlesForIssue]);

    // Fetch latest issue on mount (only if not pre-populated from SSR)
    useEffect(() => {
        if (sortedIssueIds[0]) {
            void fetchArticlesForIssue(sortedIssueIds[0], { showLoading: true });
        }
    }, [sortedIssueIds, fetchArticlesForIssue]);

    // Warm other issue pages after the first screen has rendered.
    useEffect(() => {
        if (sortedIssueIds.length <= 1) return;

        let cancelled = false;
        const connection = typeof navigator !== 'undefined'
            ? (navigator as Navigator & { connection?: NetworkInformationLike }).connection
            : undefined;
        const warmMany = shouldWarmManyIssues(connection);
        const queue = buildIssueWarmQueue(
            sortedIssueIds,
            currentIssueIndex,
            Object.keys(articleCacheRef.current),
            {
                neighborDepth: 2,
                recentCount: warmMany ? 14 : 4,
                maxItems: warmMany ? sortedIssueIds.length : 6,
            }
        );

        const run = async () => {
            for (const issueId of queue) {
                if (cancelled) return;
                await fetchArticlesForIssue(issueId, { showLoading: false });
                if (!cancelled) await wait(warmMany ? 60 : 180);
            }
        };

        const cancelSchedule = scheduleIssueWarm(() => {
            void run();
        });

        return () => {
            cancelled = true;
            cancelSchedule();
        };
    }, [sortedIssueIds, currentIssueIndex, fetchArticlesForIssue]);

    // Firebase view stats
    const recordEditionView = useCallback((editionCode: string) => {
        if (!editionCode) return;
        fetch('/api/stats/view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ edition_code: editionCode }),
            keepalive: true
        }).catch(() => {});
    }, []);

    // GA4 + Firebase stats on first mount
    useEffect(() => {
        if (initialViewRecordedRef.current) return;
        if (currentIssue?.edition_code) {
            initialViewRecordedRef.current = true;
            sendGAEvent('event', 'edition_view', {
                edition_code: currentIssue.edition_code,
                edition_name: currentIssue.edition_name || '',
                article_count: currentIssue.article_count || 0
            });
            recordEditionView(currentIssue.edition_code);
        }
    }, [currentIssue?.article_count, currentIssue?.edition_code, currentIssue?.edition_name, recordEditionView]);

    // Issue change handler
    const handleIssueChange = useCallback((issueId: string) => {
        const newIndex = sortedIssueIds.indexOf(issueId);
        if (newIndex !== -1) {
            setCurrentIssueIndex(newIndex);
            void fetchArticlesForIssue(issueId, { showLoading: true });

            const issue = issueMap[issueId];
            if (issue?.edition_code) {
                sendGAEvent('event', 'edition_view', {
                    edition_code: issue.edition_code,
                    edition_name: issue.edition_name || '',
                    article_count: issue.article_count || 0
                });
                recordEditionView(issue.edition_code);
            }
        }
    }, [sortedIssueIds, issueMap, fetchArticlesForIssue, recordEditionView]);

    return (
        <PageFrame
            currentDate={currentIssue?.date || null}
            editionName={currentIssue?.edition_name || null}
            prevDate={prevIssue?.date || null}
            prevEditionName={prevIssue?.edition_name || null}
            prevIssueId={prevIssueId}
            nextDate={nextIssue?.date || null}
            nextEditionName={nextIssue?.edition_name || null}
            nextIssueId={nextIssueId}
            onDateChange={(target) => {
                if (sortedIssueIds.includes(target)) {
                    handleIssueChange(target);
                    return;
                }
                const matchingId = sortedIssueIds.find(id => issueMap[id]?.date === target);
                if (matchingId) handleIssueChange(matchingId);
            }}
            articles={currentArticles}
            issues={sortedIssues.map(issue => ({
                id: issue.id,
                date: issue.date,
                edition_name: issue.edition_name,
                article_count: issue.article_count
            }))}
            currentIssueId={currentIssueId}
            onIssueSelect={handleIssueChange}
            onIssuePrefetch={prefetchIssue}
        >
            {isCurrentIssueLoading ? (
                <div className="text-center py-20 text-muted-foreground">
                    <p className="text-xl">Loading...</p>
                </div>
            ) : currentIssueId && currentArticles.length > 0 ? (
                <ArticleDisplay articles={currentArticles as Article[]} loading={false} error={null} />
            ) : (
                <div className="text-center py-20 text-muted-foreground">
                    <p className="text-xl">표시할 기사가 없습니다.</p>
                    <p className="text-sm mt-2">발행된 호가 아직 없습니다.</p>
                </div>
            )}
        </PageFrame>
    );
}
